'use client';

import { useLocale } from '@/i18n/useLocale';
import { useTransition } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { localeNames, localeFlags, localeShortLabels, type Locale, locales } from '@/i18n/config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface LanguageSwitcherProps {
  className?: string;
  /**
   * 'default': Use system configured locales, hide when only 1 locale available
   * 'standalone': Use all available locales, always show (for install pages)
   */
  variant?: 'default' | 'standalone';
  /**
   * 紧凑模式:仅渲染当前语言旗帜的图标按钮(h-8 w-8,与 guided 卡顶栏图标按钮统一),
   * 下拉内容不变。用于空间受限的顶栏(guided 卡)。默认 false(完整 Globe+旗帜+名称触发器)。
   */
  compact?: boolean;
}

export function LanguageSwitcher({ className, variant = 'default', compact = false }: LanguageSwitcherProps) {
  const { locale, setLocale, supportedLocales } = useLocale();
  const [isPending, startTransition] = useTransition();

  const availableLocales: Locale[] = variant === 'standalone' ? [...locales] : supportedLocales;

  // Don't render if only one language is available (default variant only)
  if (variant === 'default' && availableLocales.length <= 1) {
    return null;
  }

  const handleLocaleChange = (newLocale: Locale) => {
    startTransition(() => {
      document.body.classList.add('locale-switching');
      setLocale(newLocale);
      setTimeout(() => {
        document.body.classList.remove('locale-switching');
      }, 300);
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={localeNames[locale]}
        className={cn(
          compact
            ? 'inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20'
            : cn(
                'group flex items-center gap-2 px-3 h-9',
                'rounded-lg shadow-sm',
                'bg-overlay/50 backdrop-blur-sm',
                'border border-border',
                'hover:bg-overlay/70',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary/20',
              ),
          className,
        )}
        disabled={isPending}
      >
        {compact ? (
          <>
            <Globe className="h-3.5 w-3.5" />
            <span>{localeShortLabels[locale]}</span>
          </>
        ) : (
          <>
            <Globe className="h-4 w-4 text-primary dark:text-primary" />
            <span className="text-lg font-semibold">{localeFlags[locale]}</span>
            <span className="hidden sm:inline text-sm font-medium text-muted-foreground ml-1">
              {localeNames[locale]}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform duration-200 data-[popup-open]:rotate-180" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        variant="default"
        className="min-w-[180px]"
      >
        {availableLocales.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => handleLocaleChange(loc)}
            className="flex items-center gap-3 px-3 py-2.5"
          >
            <span className="text-lg font-semibold">{localeFlags[loc]}</span>
            <span className="flex-1 font-medium text-foreground">{localeNames[loc]}</span>
            {locale === loc && (
              <span className="absolute right-2 flex items-center justify-center">
                <Check className="h-4 w-4 text-primary dark:text-primary" />
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
